/**
 * Get date range based on filter selection
 * @param {string} filter - Filter option (today, yesterday, last7days, etc.)
 * @returns {Object} Object with startDate and endDate
 */
export const getDateRangeFromFilter = (filter) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  
  let startDate = new Date(today);
  
  switch (filter) {
    case 'today':
      startDate = new Date(today);
      break;
      
    case 'yesterday':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 1);
      endDate.setDate(endDate.getDate() - 1);
      break;
      
    case 'last7days':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 6);
      break;
      
    case 'last30days':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 29);
      break;
      
    case 'last90days':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 89);
      break;
      
    case 'lastyear':
      startDate = new Date(today);
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
      
    default:
      // For 'custom' or unknown, return null to let user pick dates
      return { startDate: null, endDate: null };
  }
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format date and time for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '€0.00';
  return `€${Number(amount).toFixed(2)}`;
};

/**
 * Get payment method display name
 * @param {string} method - Payment method code
 * @returns {string} Display name
 */
export const getPaymentMethodDisplayName = (method) => {
  const methodMap = {
    'Cash': 'Cash',
    'Bancontact': 'Bancontact',
    'Veriment': 'Veriment',
    'Not Specified': 'Not Specified'
  };
  return methodMap[method] || method;
};

/**
 * Get source type display name
 * @param {string} sourceType - Source type code
 * @returns {string} Display name
 */
export const getSourceTypeDisplayName = (sourceType) => {
  const sourceMap = {
    'Invoice': 'Invoice',
    'Offer': 'Offer',
    'Ticket': 'Ticket'
  };
  return sourceMap[sourceType] || sourceType;
};

/**
 * Calculate payment summary from payment list
 * @param {Array} payments - Array of payment objects
 * @returns {Object} Summary statistics
 */
export const calculatePaymentSummary = (payments) => {
  if (!Array.isArray(payments) || payments.length === 0) {
    return {
      totalAmount: 0,
      totalPayments: 0,
      completedPayments: 0,
      partialPayments: 0,
      pendingPayments: 0,
      averagePayment: 0
    };
  }

  const totalAmount = payments.reduce((sum, p) => sum + (p.Amount || 0), 0);
  const totalPayments = payments.length;
  const completedPayments = payments.filter(p => p.PaymentStatus === 'Completed').length;
  const partialPayments = payments.filter(p => p.IsPartialPayment).length;
  const pendingPayments = payments.filter(p => p.PaymentStatus === 'Pending').length;
  const averagePayment = totalPayments > 0 ? totalAmount / totalPayments : 0;

  return {
    totalAmount,
    totalPayments,
    completedPayments,
    partialPayments,
    pendingPayments,
    averagePayment
  };
};

/**
 * Validate payment data
 * @param {Object} paymentData - Payment data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validatePaymentData = (paymentData) => {
  const errors = {};

  if (!paymentData.Amount || paymentData.Amount <= 0) {
    errors.Amount = 'Amount must be greater than 0';
  }

  if (!paymentData.PaymentDate) {
    errors.PaymentDate = 'Payment date is required';
  }

  if (!paymentData.CustomerId) {
    errors.CustomerId = 'Customer is required';
  }

  if (!paymentData.SourceType) {
    errors.SourceType = 'Source type is required';
  }

  if (!paymentData.SourceId) {
    errors.SourceId = 'Source document is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};